import { LinkIcon } from '@primer/octicons-react';
import { useRouter } from 'next/router';
import React, { Suspense, useEffect } from 'react';
import { graphql, useQueryLoader } from 'react-relay';

import BrandMessageExecutionConnectionDataTable from '../../components/brandMessageTask/BrandMessageExecutionConnectionDataTable';
import BrandMessagePreview from '../../components/brandMessageTask/BrandMessagePreview';
import BrandMessageTaskDescriptionList from '../../components/brandMessageTask/BrandMessageTaskDescriptionList';
import BrandMessageTaskCreateDialog from '../../components/brandMessageTemplate/BrandMessageTaskCreateDialog';
import Card from '../../components/core/Card';
import DescriptionList from '../../components/core/DescriptionList';
import DialogButton from '../../components/core/DialogButton';
import EmptyState from '../../components/core/EmptyState';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import ErrorState from '../../components/core/ErrorState';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import HorizontalDivider from '../../components/core/HorizontalDivider';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import LinkButton from '../../components/core/LinkButton';
import MutationConfirmButton from '../../components/core/MutationConfirmButton';
import PreloadedQueryRenderer from '../../components/core/PreloadedQueryRenderer';
import SQLEditor from '../../components/core/SQLEditor';
import Spinner from '../../components/core/Spinner';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import View from '../../components/core/View';
import useLazyLoadQuery from '../../hooks/useLazyLoadQuery';
import useToast from '../../hooks/useToast';
import { BrandMessageTaskId_brandMessageExecutionsQuery } from '../../relay/__generated__/BrandMessageTaskId_brandMessageExecutionsQuery.graphql';
import { BrandMessageTaskId_brandMessagePreviewQuery } from '../../relay/__generated__/BrandMessageTaskId_brandMessagePreviewQuery.graphql';
import { BrandMessageTaskId_brandMessageTaskActivateMutation } from '../../relay/__generated__/BrandMessageTaskId_brandMessageTaskActivateMutation.graphql';
import { BrandMessageTaskId_brandMessageTaskDeactivateMutation } from '../../relay/__generated__/BrandMessageTaskId_brandMessageTaskDeactivateMutation.graphql';
import { BrandMessageTaskId_brandMessageTaskQuery } from '../../relay/__generated__/BrandMessageTaskId_brandMessageTaskQuery.graphql';
import { numberWithCommas } from '../../utils/number';
import { NextPage } from '../_app';

const brandMessageTaskQuery = graphql`
  query BrandMessageTaskId_brandMessageTaskQuery($id: ID!) {
    brandMessageTask(id: $id) {
      id
      title
      description
      query
      startAt
      isActive
      period
      template {
        id
        templateCode
        templateName
        ...BrandMessageTaskCreateDialog_brandMessageTemplate
      }
      ...BrandMessageTaskDescriptionList_brandMessageTask
    }
  }
`;

const brandMessageExecutionsForBrandMessageTaskId = graphql`
  query BrandMessageTaskId_brandMessageExecutionsQuery($id: ID!) {
    brandMessageExecutions(filters: { taskId_Exact: $id }, order: { executedAt: DESC }, page: 0, pageSize: 10) {
      totalCount
      ...BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection
    }
  }
`;

const brandMessagePreviewForBrandMessageTaskId = graphql`
  query BrandMessageTaskId_brandMessagePreviewQuery($templateId: ID!, $query: String = null) {
    brandMessagePreview(templateId: $templateId, query: $query) {
      targetCount
      ...BrandMessagePreview_brandMessagePreview
    }
  }
`;
type Props = {};

const BrandMessageTaskId: NextPage<Props> = () => {
  const router = useRouter();
  const { toast } = useToast();

  const brandMessageTaskId = router.query.brandMessageTaskId as string;

  const [{ brandMessageTask }] = useLazyLoadQuery<BrandMessageTaskId_brandMessageTaskQuery>(brandMessageTaskQuery, {
    id: brandMessageTaskId,
  });

  const [brandMessageExecutionsQueryReference, loadbrandMessageExecutionsQuery] =
    useQueryLoader<BrandMessageTaskId_brandMessageExecutionsQuery>(brandMessageExecutionsForBrandMessageTaskId);
  useEffect(() => {
    if (brandMessageTaskId) {
      loadbrandMessageExecutionsQuery({ id: brandMessageTaskId });
    }
  }, []);

  const [previewQueryReference, loadPreviewQuery] = useQueryLoader<BrandMessageTaskId_brandMessagePreviewQuery>(
    brandMessagePreviewForBrandMessageTaskId,
  );
  useEffect(() => {
    if (brandMessageTaskId && brandMessageTask.query) {
      loadPreviewQuery({ templateId: brandMessageTask.template.id, query: brandMessageTask.query });
    }
  }, []);

  if (!brandMessageTask) return null;

  const { isActive, template, title, query, startAt, period, description } = brandMessageTask;

  return (
    <View>
      <Head siteTitle={`브랜드 메세지 | ${title}`} />
      <View>
        <Grid sx={{ alignItems: 'center' }}>
          <Grid.Unit size={'max'}>
            <Text as={'h1'}>{title}</Text>
          </Grid.Unit>
          <Grid.Unit size={'min'}>
            <Stack gapX={2}>
              <Stack.Item>
                <DialogButton
                  size={'large'}
                  renderDialog={({ isOpen, closeDialog }) => (
                    <BrandMessageTaskCreateDialog
                      onDismiss={closeDialog}
                      isOpen={isOpen}
                      brandMessageTemplate={template}
                      initialValues={{
                        title: title + ' (복제)',
                        description,
                        query,
                        startAt,
                        period: period || undefined,
                      }}
                      wide
                      config={{
                        onCompleted: ({ brandMessageTaskCreate: { id } }) => {
                          toast('복제가 완료됐어요', 'success');
                          router.push(`/brandMessageTask/${id}`);
                          closeDialog();
                        },
                        onError: () => {
                          toast('복제에 실패했어요', 'error');
                        },
                      }}
                    />
                  )}
                >
                  복제하기
                </DialogButton>
              </Stack.Item>
              <Stack.Item>
                {isActive ? (
                  <MutationConfirmButton<BrandMessageTaskId_brandMessageTaskDeactivateMutation>
                    size={'large'}
                    variant={'danger'}
                    input={{ id: brandMessageTaskId }}
                    mutation={graphql`
                      mutation BrandMessageTaskId_brandMessageTaskDeactivateMutation(
                        $input: BrandMessageTaskDeactivateInput!
                      ) {
                        brandMessageTaskDeactivate(input: $input) {
                          id
                          isActive
                        }
                      }
                    `}
                    message={'브랜드 메세지를 비활성화할까요?'}
                    config={{
                      onCompleted: () => {
                        toast('브랜드 메세지가 비활성화됐어요', 'success');
                      },
                      onError: () => {
                        toast('다시 비활성화해주세요', 'error');
                      },
                    }}
                  >
                    비활성화하기
                  </MutationConfirmButton>
                ) : (
                  <MutationConfirmButton<BrandMessageTaskId_brandMessageTaskActivateMutation>
                    size={'large'}
                    variant={'primary'}
                    input={{ id: brandMessageTaskId }}
                    mutation={graphql`
                      mutation BrandMessageTaskId_brandMessageTaskActivateMutation(
                        $input: BrandMessageTaskActivateInput!
                      ) {
                        brandMessageTaskActivate(input: $input) {
                          id
                          isActive
                        }
                      }
                    `}
                    message={'브랜드 메세지를 활성화할까요?'}
                    config={{
                      onCompleted: () => {
                        toast('브랜드 메세지가 활성화됐어요', 'success');
                      },
                      onError: () => {
                        toast('다시 활성화해주세요', 'error');
                      },
                    }}
                  >
                    활성화하기
                  </MutationConfirmButton>
                )}
              </Stack.Item>
            </Stack>
          </Grid.Unit>
        </Grid>
        <HorizontalDivider mt={[3, 3, 0]} mb={5} />
        <Grid gapX={5} gapY={3} reverse={[true, true, false]}>
          <Grid.Unit size={[1, 1, 3 / 4]}>
            <View sx={{ marginTop: 5 }}>
              <Card sx={{ padding: 4 }}>
                <BrandMessageTaskDescriptionList
                  brandMessageTask={brandMessageTask}
                  titleUnitSize={1 / 4}
                  descriptionUnitSize={3 / 4}
                />
              </Card>
            </View>
            <View sx={{ marginTop: 5 }}>
              <View mb={1}>
                <Text fontSize={1}>SQL</Text>
              </View>
              <SQLEditor readOnly value={query} />
            </View>

            <View sx={{ marginTop: 5 }}>
              <Text fontSize={3} fontWeight={'bold'}>
                메세지 미리보기
              </Text>
              <ErrorBoundary
                skipSentry
                key={previewQueryReference?.fetchKey}
                fallback={({ parsedGraphQLError }) => {
                  return (
                    <View sx={{ padding: 5 }}>
                      <ErrorState
                        description={
                          '새로고침하거나 잠시 후 시도해주세요' +
                          (parsedGraphQLError?.[0].message ? `\n${parsedGraphQLError?.[0].message}` : '')
                        }
                      />
                    </View>
                  );
                }}
              >
                {previewQueryReference ? (
                  <Suspense
                    fallback={
                      <View py={10}>
                        <Spinner />
                      </View>
                    }
                  >
                    <PreloadedQueryRenderer
                      query={brandMessagePreviewForBrandMessageTaskId}
                      queryReference={previewQueryReference}
                    >
                      {({ brandMessagePreview }) => {
                        return (
                          <View sx={{ marginTop: 3 }}>
                            <Grid gapX={3} gapY={3} wrap={[true, true, false]}>
                              <Grid.Unit size={[1, 1, 'min']}>
                                <View mx={'auto'} maxWidth={320}>
                                  <BrandMessagePreview hideTo brandMessagePreview={brandMessagePreview} />
                                </View>
                              </Grid.Unit>
                              <Grid.Unit size={'max'}>
                                <Card sx={{ padding: 4 }}>
                                  <DescriptionList
                                    item={brandMessageTask}
                                    itemDescriptions={{
                                      targetCount: {
                                        title: '발송 대상',
                                        renderValue: () => (
                                          <Text fontSize={1}>
                                            {numberWithCommas(brandMessagePreview.targetCount)}명
                                          </Text>
                                        ),
                                      },
                                      template: {
                                        title: '템플릿',
                                        renderValue: ({ template: { id, templateName, templateCode } }) => (
                                          <View>
                                            <LinkButton
                                              href={`/brandMessageTemplate/${id}`}
                                              size={'small'}
                                              variant={'invisible'}
                                              leadingIcon={LinkIcon}
                                            >
                                              {templateName}
                                            </LinkButton>
                                            <Text fontSize={1} sx={{ wordBreak: 'break-all' }}>
                                              {templateCode}
                                            </Text>
                                          </View>
                                        ),
                                      },
                                    }}
                                    picks={['targetCount', 'template']}
                                    titleUnitSize={1 / 4}
                                    descriptionUnitSize={3 / 4}
                                  />
                                </Card>
                              </Grid.Unit>
                            </Grid>
                          </View>
                        );
                      }}
                    </PreloadedQueryRenderer>
                  </Suspense>
                ) : null}
              </ErrorBoundary>
            </View>

            <View sx={{ marginTop: 5 }}>
              <Text fontSize={3} fontWeight={'bold'}>
                최근 발송 이력
              </Text>
            </View>
            <View sx={{ marginTop: 3 }}>
              <ErrorBoundary
                fallback={({ parsedGraphQLError }) => {
                  return (
                    <View sx={{ padding: 5 }}>
                      <ErrorState
                        description={
                          '새로고침하거나 잠시 후 시도해주세요' +
                          (parsedGraphQLError?.[0].message ? `\n${parsedGraphQLError?.[0].message}` : '')
                        }
                      />
                    </View>
                  );
                }}
              >
                {brandMessageExecutionsQueryReference ? (
                  <Suspense
                    fallback={
                      <View py={10}>
                        <Spinner />
                      </View>
                    }
                  >
                    <PreloadedQueryRenderer<BrandMessageTaskId_brandMessageExecutionsQuery>
                      query={brandMessageExecutionsForBrandMessageTaskId}
                      queryReference={brandMessageExecutionsQueryReference}
                    >
                      {({ brandMessageExecutions }) => {
                        return (
                          <View sx={{ marginTop: 3 }}>
                            <BrandMessageExecutionConnectionDataTable
                              brandMessageExecutionConnection={brandMessageExecutions}
                              emptyState={
                                <View sx={{ paddingY: 3 }}>
                                  <EmptyState title={'발송 이력이 없어요'} description={''} />
                                </View>
                              }
                            />
                          </View>
                        );
                      }}
                    </PreloadedQueryRenderer>
                  </Suspense>
                ) : null}
              </ErrorBoundary>
            </View>
          </Grid.Unit>
          <Grid.Unit size={[1, 1, 1 / 4]}>
            <BrandMessageTaskDescriptionList
              brandMessageTask={brandMessageTask}
              type={'activity'}
              titleUnitSize={'max'}
              descriptionUnitSize={'min'}
              renderTitle={(title) => <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>{title}</Text>}
            />
          </Grid.Unit>
        </Grid>
      </View>
    </View>
  );
};

BrandMessageTaskId.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
BrandMessageTaskId.authenticated = true;
BrandMessageTaskId.routes = [
  {
    id: 'brandMessageTaskId',
    pathname: '/brandMessageTask/[brandMessageTaskId]',
    name: '브랜드 메세지 상세',
    permissions: ['app_push_read'],
  },
];

export default BrandMessageTaskId;
